import { createDrawingCanvas, getDrawingContext2d } from '@/core/drawing-canvas'
import { performanceDiagnostics } from './performance-diagnostics'

export interface SourceFrame {
  readonly id: number
  readonly capturedAt: number
  /** Browser capture/presentation time, retained separately for evidence age. */
  readonly sourceCapturedAt?: number
  readonly observedAt?: number
  readonly timestampSource?: 'capture' | 'presentation' | 'observed'
}

type FirefoxVideoElement = HTMLVideoElement & {
  webkitDecodedFrameCount?: number
  mozPresentedFrames?: number
}

function usableFrameCount(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
}

/** One identity across regular and priority sampling; retains metadata only. */
export class VideoFrameSource {
  private readonly releaseDiagnostic = performanceDiagnostics.acquire('frame-sources')
  private callbackId: number | null = null
  private disposed = false
  private counter: number | null = null
  private presented: number | null = null
  private fingerprint: number | null = null
  private current: SourceFrame | null = null
  private lastCallbackAt = 0
  private lastFingerprintAt = -Infinity
  private fallbackSinceCallback = false

  constructor(private readonly video: HTMLVideoElement, private readonly fallbackDelayMs: () => number = () => 500,
    private readonly options: { now?: () => number; onFrame?: () => void } = {}) {
    this.lastCallbackAt = this.now()
    this.read()
    this.subscribe()
  }

  read(): SourceFrame | null {
    if (this.disposed || !this.video.videoWidth || !this.video.videoHeight) return null
    const counter = this.frameCount()
    if (counter !== null) {
      if (this.counter === null || counter !== this.counter) {
        this.advance()
        this.fallbackSinceCallback = true
      }
      this.counter = counter
    } else if (this.now() - this.lastFingerprintAt >= 500) {
      const fallbackAllowed = !this.video.requestVideoFrameCallback || this.now() - this.lastCallbackAt >= this.fallbackDelayMs()
      if (!fallbackAllowed && this.fingerprint !== null) return this.current
      const fingerprint = this.imageFingerprint()
      if (fallbackAllowed && fingerprint !== null && fingerprint !== this.fingerprint
        && (!this.video.requestVideoFrameCallback || this.fingerprint !== null)) {
        this.advance()
        this.fallbackSinceCallback = true
      }
      this.fingerprint = fingerprint
      this.lastFingerprintAt = this.now()
    }
    return this.current
  }

  async waitAfter(id: number, signal: AbortSignal): Promise<SourceFrame | null> {
    while (!signal.aborted && !this.disposed) {
      const frame = this.read()
      if (frame && frame.id > id) return frame
      await new Promise<void>((resolve) => {
        const done = () => { clearTimeout(timer); signal.removeEventListener('abort', done); resolve() }
        const timer = setTimeout(done, 50)
        signal.addEventListener('abort', done, { once: true })
      })
    }
    return null
  }

  dispose(): void {
    this.releaseDiagnostic()
    this.disposed = true
    if (this.callbackId !== null) this.video.cancelVideoFrameCallback?.(this.callbackId)
    this.callbackId = null
  }

  private subscribe(): void {
    if (this.disposed || !this.video.requestVideoFrameCallback) return
    this.callbackId = this.video.requestVideoFrameCallback((_now, metadata) => {
      this.callbackId = null
      if (this.disposed) return
      const count = this.frameCount()
      const presented = usableFrameCount(metadata?.presentedFrames)
      // A fallback may already have observed this frame. Counters take precedence when they actually move.
      const fallbackAlreadySawFrame = this.fallbackSinceCallback && (count !== null
        ? count === this.counter
        : this.fingerprint !== null && this.imageFingerprint() === this.fingerprint)
      const countMoved = count !== null && count !== this.counter
      const presentedMoved = presented !== null && presented !== this.presented
        && (count === null || this.presented !== null)
      const identityMoved = countMoved || presentedMoved || (count === null && presented === null)
      if (!fallbackAlreadySawFrame && identityMoved) this.advance(metadata)
      else if (fallbackAlreadySawFrame && this.current) {
        const timing = this.sourceTiming(metadata)
        // Late metadata can reveal older evidence, but must never refresh the
        // frame identity, observation time, or already known evidence age.
        if (timing.timestampSource !== 'observed'
          && timing.sourceCapturedAt <= (this.current.sourceCapturedAt ?? this.current.capturedAt)) {
          this.current = { ...this.current, ...timing }
        }
      }
      this.counter = count
      this.presented = presented
      this.lastCallbackAt = this.now()
      this.fallbackSinceCallback = false
      this.fingerprint = null
      this.lastFingerprintAt = -Infinity
      this.subscribe()
      this.options.onFrame?.()
    })
  }

  private now(): number { return this.options.now?.() ?? Date.now() }

  private advance(metadata?: VideoFrameCallbackMetadata): void {
    const observedAt = this.now()
    this.current = { id: (this.current?.id ?? 0) + 1,
      capturedAt: Math.max(observedAt, (this.current?.capturedAt ?? 0) + 1),
      observedAt, ...this.sourceTiming(metadata) }
  }

  private sourceTiming(metadata?: VideoFrameCallbackMetadata): Pick<SourceFrame, 'sourceCapturedAt' | 'timestampSource'> & { sourceCapturedAt: number } {
    const observedAt = this.now(), monotonicNow = performance.now()
    const captureTime = (metadata as (VideoFrameCallbackMetadata & { captureTime?: number }) | undefined)?.captureTime
    const valid = (value: number | undefined): value is number => value !== undefined && Number.isFinite(value) && value >= 0 && value <= monotonicNow
    const timestampSource = valid(captureTime) ? 'capture' : valid(metadata?.presentationTime) ? 'presentation' : 'observed'
    const sourceTime = timestampSource === 'capture' ? captureTime! : timestampSource === 'presentation' ? metadata!.presentationTime : monotonicNow
    // Convert browser monotonic metadata to the consumer's clock. Reading the
    // same buffered frame later must never make its evidence younger.
    return { sourceCapturedAt: observedAt - (monotonicNow - sourceTime), timestampSource }
  }

  private frameCount(): number | null {
    try {
      const element = this.video as FirefoxVideoElement
      return usableFrameCount(element.getVideoPlaybackQuality?.().totalVideoFrames)
        ?? usableFrameCount(element.webkitDecodedFrameCount)
        ?? usableFrameCount(element.mozPresentedFrames)
    } catch { return null }
  }

  private imageFingerprint(): number | null {
    try {
      const canvas = createDrawingCanvas(16, 9)
      const context = getDrawingContext2d(canvas, { willReadFrequently: true })
      context.drawImage(this.video, 0, 0, 16, 9)
      const bytes = context.getImageData(0, 0, 16, 9).data
      let hash = 2_166_136_261
      for (const byte of bytes) hash = Math.imul(hash ^ byte, 16_777_619)
      return hash >>> 0
    } catch { return null }
  }
}
